/**
 * A dynamic-import / chunk-load failure: the app requested a JS or CSS chunk the
 * server no longer has (a stale index.html still points at old chunk hashes after
 * a deploy), a `<link rel="modulepreload">` failed, or the network dropped
 * mid-request. Each engine phrases the same failure differently, so recovery code
 * that matched only one phrasing left the others white-screening. This is the
 * single source of truth for every recovery site — the router-level
 * RootErrorBoundary, the inner component-level ErrorBoundary (which wraps the
 * designer/dashboard — the primary paid surface), and the global handlers in
 * main.tsx — so the matchers can't drift apart again.
 *
 *   - Chrome:    "Failed to fetch dynamically imported module"
 *   - Firefox:   "error loading dynamically imported module"
 *   - Safari:    "Importing a module script failed"
 *   - a chunk whose bytes lack an export its importer links against (see
 *     MODULE_LINK_RE): Safari "Importing binding name '…' is not found",
 *     Chrome "does not provide an export named", Firefox "import not found"
 *   - webpack-style: "ChunkLoadError" / "Loading chunk N failed"
 *   - preloaded CSS: "Unable to preload CSS"
 */
export const CHUNK_LOAD_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Importing binding name|does not provide an export named|import not found|ChunkLoadError|Loading chunk|Unable to preload CSS/i;

/**
 * Link-time failure: every chunk arrived, but one of them has the wrong bytes.
 * Hashed names never change content between builds, so the bad copy sits in
 * the browser's HTTP cache (an empty 200 reproduces it exactly), and assets are
 * served `immutable`, so neither reload nor a fresh navigation re-asks the
 * server. Seen on Safari 26.4, err_muia88bd_9v6d77.
 */
export const MODULE_LINK_RE = /Importing binding name|does not provide an export named|import not found/i;

export function isChunkLoadError(error: unknown): boolean {
  const msg = errorMessage(error);
  return CHUNK_LOAD_RE.test(msg);
}

export function isModuleLinkError(error: unknown): boolean {
  return MODULE_LINK_RE.test(errorMessage(error));
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : String((error as { message?: string } | null | undefined)?.message ?? error ?? '');
}

/**
 * Shared reload budget. Recovery used to live in two places with two separate
 * session keys: RootErrorBoundary spent from `chunk-reload-count` (2 attempts,
 * service-worker purge before reload) while the inner ErrorBoundary and the
 * global handlers in main.tsx spent from `chunk-error-reload` (1 attempt, no
 * purge). Whichever fired first burned an attempt the other could not see, so a
 * single transient fetch failure could drop a user straight onto the error
 * screen with no retry at all (err_ms3ghku0_1pxzol, dashboard, 2026-07-27:
 * the chunk was present and healthy on the server the whole time). One key,
 * one budget, one purge path.
 */
const RELOAD_BUDGET_KEY = 'chunk-reload-count';
/**
 * One failed chunk usually surfaces twice: once as the Vite preload event and
 * again as the rejection React re-throws through the error boundary. Without
 * this guard the second report spends a second unit of a two-unit budget, so a
 * single incident exhausts recovery and the next real failure goes straight to
 * the error screen.
 */
let recoveryInFlight = false;

export const MAX_CHUNK_RELOADS = 2;

/** sessionStorage is null or throws in private mode and when storage is blocked
 *  (digest 2026-06-28), so the counter is mirrored in memory. Recovery code must
 *  never itself throw. */
let memoryReloadCount = 0;

export function getChunkReloadCount(): number {
  try {
    const stored = parseInt(sessionStorage.getItem(RELOAD_BUDGET_KEY) || '0', 10);
    return Math.max(Number.isFinite(stored) ? stored : 0, memoryReloadCount);
  } catch {
    return memoryReloadCount;
  }
}

export function canRetryChunkLoad(): boolean {
  return getChunkReloadCount() < MAX_CHUNK_RELOADS;
}

/** Called once the user is looking at real content again, so a later deploy in
 *  the same session gets a full budget instead of inheriting a spent one. */
export function resetChunkReloadCount(): void {
  memoryReloadCount = 0;
  // Clears the in-flight latch too: a successful load means the incident is
  // over, and tests reset through here between cases.
  recoveryInFlight = false;
  try { sessionStorage.removeItem(RELOAD_BUDGET_KEY); } catch { /* storage blocked */ }
}

function spendChunkReload(): void {
  memoryReloadCount = getChunkReloadCount() + 1;
  try { sessionStorage.setItem(RELOAD_BUDGET_KEY, String(memoryReloadCount)); } catch { /* storage blocked */ }
}

/** Drop every service-worker cache and registration. A chunk failure is often a
 *  poisoned cache entry (HTML served under a .js URL by an edge interstitial, or
 *  a pre-deploy chunk), and a plain reload just re-serves the same bad bytes. */
export async function purgeServiceWorker(): Promise<void> {
  try {
    if (typeof window !== 'undefined' && 'caches' in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys.filter((k) => k.startsWith('cctvplanner-')).map((k) => caches.delete(k)),
      );
    }
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* best effort, the caller reloads regardless */
  }
}

/**
 * Re-download this document's own JS chunks past the HTTP cache. A
 * `cache: 'reload'` fetch overwrites the cached entry the next document links
 * from, which is the only thing that cures a bad immutable copy (verified in
 * WebKit: reload and navigation both kept failing, this fixed it).
 */
export async function refreshCachedChunks(): Promise<void> {
  try {
    const urls = new Set<string>();
    for (const entry of performance.getEntriesByType('resource')) urls.add(entry.name);
    document
      .querySelectorAll<HTMLLinkElement>('link[rel="modulepreload"][href]')
      .forEach((link) => urls.add(link.href));
    document
      .querySelectorAll<HTMLScriptElement>('script[type="module"][src]')
      .forEach((script) => urls.add(script.src));
    const chunks = [...urls].filter((u) => {
      try {
        const url = new URL(u, window.location.href);
        return url.origin === window.location.origin && url.pathname.startsWith('/assets/') && url.pathname.endsWith('.js');
      } catch {
        return false;
      }
    });
    await Promise.all(
      chunks.map((u) => fetch(u, { cache: 'reload' }).then((r) => r.arrayBuffer()).catch(() => undefined)),
    );
  } catch {
    /* best effort, the caller reloads regardless */
  }
}

/**
 * Spend one unit of the shared budget: purge the service worker (and, for a
 * link error, refresh the cached chunks), then hard reload so the fresh
 * index.html points at current chunks. Returns false when the budget is
 * exhausted so the caller falls through to its error UI instead of looping.
 * The reload runs after the async purge, so callers must treat this as
 * "recovery started", not "page already gone".
 */
/** A recovery reload has started and the page is about to go. */
export function isChunkRecoveryInFlight(): boolean {
  return recoveryInFlight;
}

export function recoverFromChunkError(error?: unknown): boolean {
  if (typeof window === 'undefined') return false;
  // Already reloading for this incident: report success without re-spending.
  if (recoveryInFlight) return true;
  if (!canRetryChunkLoad()) return false;
  recoveryInFlight = true;
  spendChunkReload();
  void (async () => {
    // Do NOT purge while offline. The service-worker caches are the only thing
    // that can still answer the reload, and the in-place retries filter toward
    // sustained outages rather than away from them: a blip is handled before we
    // ever get here. Purging then would trade a recoverable error screen for a
    // blank one. Online, the purge is the point, since a poisoned cache entry
    // survives a plain reload.
    if (navigator.onLine !== false) {
      await purgeServiceWorker();
      if (isModuleLinkError(error)) await refreshCachedChunks();
    }
    window.location.reload();
  })();
  return true;
}
